import {
  CalendarEvent,
  ICalendarEvent,
  ICalendarEventJS,
} from '@app/models/CalendarEvent'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import moment from 'moment'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum BrokeragePipelinesCalendarScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IBrokeragePipelinesCalendarState {
  readonly downloadUrl: string
  readonly endTime: number
  readonly error?: string
  readonly events: List<ICalendarEvent>
  readonly isLoading: boolean
  readonly scope: BrokeragePipelinesCalendarScope
  readonly startTime: number
}

const BrokeragePipelinesCalendarStateRecord = Record({
  downloadUrl: '',
  endTime: 0,
  error: undefined,
  events: List<CalendarEvent>(),
  isLoading: false,
  scope: BrokeragePipelinesCalendarScope.Mine,
  startTime: 0,
})

export class BrokeragePipelinesCalendarState
  extends BrokeragePipelinesCalendarStateRecord
  implements IBrokeragePipelinesCalendarState {
  public static fromJS(
    initialState?: Partial<IBrokeragePipelinesCalendarState>,
  ) {
    if (!initialState) {
      return new BrokeragePipelinesCalendarState()
    }

    const { events, ...rest } = initialState

    return new BrokeragePipelinesCalendarState({
      events: events
        ? List(events.map((e) => new CalendarEvent(e)))
        : List<CalendarEvent>(),
      ...rest,
    })
  }

  public readonly downloadUrl: string
  public readonly endTime: number
  public readonly error?: string
  public readonly events: List<CalendarEvent>
  public readonly isLoading: boolean
  public readonly scope: BrokeragePipelinesCalendarScope
  public readonly startTime: number

  constructor(props?: Partial<BrokeragePipelinesCalendarState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum BrokeragePipelinesCalendarActionTypes {
  LoadEvents = 'BROKERAGE_PIPELINE_CALENDAR::LOAD_EVENTS',
  LoadEventsFailure = 'BROKERAGE_PIPELINE_CALENDAR::LOAD_EVENTS_FAILURE',
  LoadEventsRequest = 'BROKERAGE_PIPELINE_CALENDAR::LOAD_EVENTS_REQUEST',
  LoadEventsSuccess = 'BROKERAGE_PIPELINE_CALENDAR::LOAD_EVENTS_SUCCESS',
  SetScope = 'BROKERAGE_PIPELINE_CALENDAR::SET_SCOPE',
}

interface ILoadEventsAction {
  end: number
  start: number
  type: BrokeragePipelinesCalendarActionTypes.LoadEvents
}

interface ILoadEventsFailureAction {
  error: string
  type: BrokeragePipelinesCalendarActionTypes.LoadEventsFailure
}

interface ILoadEventsRequestAction {
  end: number
  start: number
  type: BrokeragePipelinesCalendarActionTypes.LoadEventsRequest
}

interface ILoadEventsSuccessAction {
  downloadUrl: string
  events: CalendarEvent[]
  type: BrokeragePipelinesCalendarActionTypes.LoadEventsSuccess
}

interface ISetScopeAction {
  scope: BrokeragePipelinesCalendarScope
  type: BrokeragePipelinesCalendarActionTypes.SetScope
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadEventsFailureAction
  | ILoadEventsRequestAction
  | ILoadEventsSuccessAction
  | ISetScopeAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadEvents(start: number, end: number): ILoadEventsAction {
  return {
    end,
    start,
    type: BrokeragePipelinesCalendarActionTypes.LoadEvents,
  }
}

function loadEventsFailure(error: string): ILoadEventsFailureAction {
  return {
    error,
    type: BrokeragePipelinesCalendarActionTypes.LoadEventsFailure,
  }
}

function loadEventsRequest(
  start: number,
  end: number,
): ILoadEventsRequestAction {
  return {
    end,
    start,
    type: BrokeragePipelinesCalendarActionTypes.LoadEventsRequest,
  }
}

function loadEventsSuccess(
  events: CalendarEvent[],
  downloadUrl: string,
): ILoadEventsSuccessAction {
  return {
    downloadUrl,
    events,
    type: BrokeragePipelinesCalendarActionTypes.LoadEventsSuccess,
  }
}

function setScope(scope: BrokeragePipelinesCalendarScope): ISetScopeAction {
  return {
    scope,
    type: BrokeragePipelinesCalendarActionTypes.SetScope,
  }
}

export const actionCreators = {
  loadEvents,
  setScope,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new BrokeragePipelinesCalendarState()

export const reducer: Reducer<BrokeragePipelinesCalendarState> = (
  state: BrokeragePipelinesCalendarState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case BrokeragePipelinesCalendarActionTypes.LoadEventsFailure:
      return state
        .set('error', undefined)
        .set('isLoading', false) as BrokeragePipelinesCalendarState

    case BrokeragePipelinesCalendarActionTypes.LoadEventsRequest:
      return state
        .set('startTime', action.start)
        .set('endTime', action.end)
        .set('isLoading', true) as BrokeragePipelinesCalendarState

    case BrokeragePipelinesCalendarActionTypes.LoadEventsSuccess:
      return state
        .set('downloadUrl', action.downloadUrl)
        .set('events', List(action.events))
        .set('isLoading', false) as BrokeragePipelinesCalendarState

    case BrokeragePipelinesCalendarActionTypes.SetScope:
      return state.set(
        'scope',
        action.scope,
      ) as BrokeragePipelinesCalendarState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadEventsSaga(action: ILoadEventsAction) {
  try {
    yield put(loadEventsRequest(action.start, action.end))

    const scope: BrokeragePipelinesCalendarScope = yield select(selectScope())

    const query = [
      `start=${encodeURIComponent(moment.unix(action.start).toISOString())}`,
      `end=${encodeURIComponent(moment.unix(action.end).toISOString())}`,
    ]

    const baseUrl =
      scope === BrokeragePipelinesCalendarScope.All
        ? '/api/Calendars/BrokeragePipelines'
        : '/api/Calendars/BrokeragePipelines/My'

    const result = yield makeAuthenticatedRequest(
      `${baseUrl}?${query.join('&')}`,
    )

    if (result.success === true) {
      const events = result.results.map((c: ICalendarEventJS) =>
        CalendarEvent.fromJS(c),
      )
      yield put(loadEventsSuccess(events, result.downloadUrl))
    } else {
      yield put(loadEventsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadEventsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadEvents() {
  yield takeLatest(
    BrokeragePipelinesCalendarActionTypes.LoadEvents,
    loadEventsSaga,
  )
}

export function* brokeragePipelinesCalendarRootSaga() {
  yield fork(watchLoadEvents)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.brokeragePipelinesCalendar

export const selectDownloadUrl = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.downloadUrl,
  )

export const selectEndTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.endTime,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectEvents = () =>
  createSelector(
    selectModuleState(),
    (mState) => {
      return mState.events
    },
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectStartTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.startTime,
  )
